import React from "react";
import { Theme } from "@mui/material";

export const createFeatureRequestBannerStyles = (theme: Theme) => ({
  banner: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "#072531",
    py: 2,
    boxShadow: "0 -4px 6px -1px rgba(0, 0, 0, 0.1)",
    zIndex: 50,
    height: { xs: "33vh", md: "256px" },
    overflowY: { xs: "auto", md: "hidden" },
  },

  container: {
    height: "100%",
    position: "relative",
  },

  closeButton: {
    position: "absolute",
    top: -15,
    right: 12,
    color: "white",
    fontSize: "1.125rem",
    fontWeight: 700,
    borderRadius: 1.5,
    p: 1,
    "&:hover": {
      opacity: 0.5,
    },
  },

  content: {
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    justifyContent: "space-between",
    alignItems: "flex-start",
    height: "100%",
    px: "2%",
    pt: { xs: 4, md: 0 },
  },

  card: {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    borderRadius: 1,
    overflow: "hidden",
    width: "100%",
  },

  cardHeader: {
    py: 0.7,
    px: 1,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
    },
    transition: "background-color 0.2s ease-in-out",
  },

  cardTitleSection: {
    display: "flex",
    alignItems: "center",
    gap: 1,
  },

  cardExpandIcon: (isExpanded: boolean) => ({
    width: 20,
    height: 20,
    color: "rgba(255, 255, 255, 0.7)",
    transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
    transition: "transform 0.3s ease-in-out",
  }),

  cardTitleText: {
    color: "white",
    fontWeight: "bold",
    fontSize: "0.875rem",
    lineHeight: 1,
  },

  cardVoteSection: {
    display: "flex",
    alignItems: "center",
    gap: 1,
  },

  cardVoteCount: {
    color: "rgba(255, 255, 255, 0.7)",
    fontSize: "0.875rem",
    fontWeight: "bold",
    lineHeight: 1,
  },

  cardVoteButton: (userHasVoted: boolean) => ({
    width: 32,
    height: 32,
    backgroundColor: userHasVoted
      ? theme.palette.materiom.seaweed
      : "rgba(255, 255, 255, 0.1)",
    color: theme.palette.materiom.eggshell,
    "&:hover": {
      backgroundColor: theme.palette.materiom.seaweed,
      opacity: 0.8,
    },
  }),

  cardDescriptionContainer: (isExpanded: boolean) => ({
    transition: "all 0.3s ease-in-out",
    maxHeight: isExpanded ? "200px" : "0px",
    opacity: isExpanded ? 1 : 0,
    overflow: "hidden",
  }),

  cardDescription: {
    p: "8px",
    px: "40px",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
  },

  cardDescriptionText: {
    color: "rgba(255, 255, 255, 0.8)",
    fontSize: "0.875rem",
    lineHeight: 1.4,
  },

  listContainer: {
    width: "100%",
    md: { width: "50%" },
    height: "100%",
  },

  listWrapper: {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    borderRadius: 0.5,
    p: 2,
    height: "100%",
  },

  listTitle: {
    color: theme.palette.materiom.eggshell,
    fontSize: "1.125rem",
    fontWeight: 600,
    mb: 1.5,
  },

  listScrollContainer: {
    height: "calc(100% - 2.75rem)",
    overflowY: "auto",
  },

  listItems: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
  },

  submissionContainer: {
    width: "100%",
    md: { width: "50%" },
    pr: { md: 2 },
    mb: { xs: 2, md: 0 },
    height: "100%",
  },

  submissionWrapper: {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    borderRadius: 0.5,
    p: 2,
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },

  submissionTitle: {
    color: theme.palette.materiom.eggshell,
    fontSize: "1.125rem",
    fontWeight: 600,
    mb: 1.5,
  },

  submissionError: {
    color: "#ef4444",
    fontSize: "0.875rem",
    mb: 1.5,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
  },

  submissionErrorIcon: {
    mr: 1,
  },

  submissionContent: {
    flex: 1,
    overflowY: "auto",
  },

  submissionForm: {
    position: "relative",
    height: "100%",
  },

  submissionFields: {
    display: "flex",
    flexDirection: "column",
    gap: 1.5,
  },

  submissionFieldWrapper: {
    position: "relative",
  },

  submissionTitleField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.palette.materiom.eggshell,
      height: "32px",
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.materiom.seaweed,
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "6px 12px",
      fontSize: "0.875rem",
    },
  },

  submissionDescriptionField: {
    "& .MuiInputBase-root": {
      padding: "0px 0px",
      height: "50px",
    },
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.palette.materiom.eggshell,
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.materiom.seaweed,
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "6px 12px",
      fontSize: "0.875rem",
    },
  },

  submissionCharCount: {
    position: "absolute",
    right: 8,
    fontSize: "0.75rem",
    color: "rgba(0, 0, 0, 0.6)",
    pointerEvents: "none",
  },

  submissionCharCountTitle: {
    top: "50%",
    transform: "translateY(-50%)",
  },

  submissionCharCountDescription: {
    bottom: 8,
  },

  submissionButton: {
    height: "40px",
    backgroundColor: theme.palette.materiom.seaweed,
    color: theme.palette.materiom.eggshell,
    borderRadius: 0.5,
    fontSize: "0.875rem",
    fontWeight: 700,
    textTransform: "none",
    "&:hover": {
      backgroundColor: theme.palette.materiom.seaweed,
    },
    "&:disabled": {
      backgroundColor: theme.palette.materiom.seaweed,
      color: theme.palette.materiom.eggshell,
      cursor: "not-allowed",
    },
  },

  submissionButtonIcon: {
    width: 24,
    height: 24,
    "@keyframes pulse": {
      "0%, 100%": { opacity: 1 },
      "50%": { opacity: 0.5 },
    },
  },
});
